// hooks/useEmployeeManagementLogic.js
import { useState, useEffect, useMemo } from "react";
import employeeApi from "services/api/employeeApi";

const useEmployeeManagementLogic = () => {
  // State variables
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [employmentTypeFilter, setEmploymentTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [notification, setNotification] = useState(null);
  const [modalState, setModalState] = useState({
    isOpen: false,
    employee: null,
    mode: "view",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isUnknownFilter, setIsUnknownFilter] = useState("");

  // Notification functions
  const closeNotification = () => {
    setNotification(null);
  };

  const showNotification = (message, type = "info") => {
    setNotification({ message, type });
  };

  // Data loading functions
  const loadEmployees = async () => {
    try {
      setLoading(true);
      const employeesData = await employeeApi.getEmployees();
      setEmployees(employeesData);
    } catch (error) {
      console.error("Error fetching employees:", error);
      showNotification("Failed to fetch employees", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = async () => {
    try {
      const departmentsData = await employeeApi.getDepartments();
      setDepartments(departmentsData);
    } catch (error) {
      console.error("Error fetching departments:", error);
      showNotification("Failed to fetch departments", "error");
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadEmployees();
    loadDepartments();
  }, []);

  // Filter and sort employees - only show known employees (non-strangers)
  const filteredAndSortedEmployees = useMemo(() => {
    let filtered = employees?.filter((employee) => {
      const fullName = `${employee?.first_name} ${employee?.last_name}`.toLowerCase();
      const matchesSearch =
        !searchTerm ||
        fullName.includes(searchTerm.toLowerCase()) ||
        employee?.employee_id?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
        employee?.email?.toLowerCase()?.includes(searchTerm.toLowerCase());

      const matchesDepartment = !departmentFilter || employee?.department === departmentFilter;
      const matchesEmploymentType =
        !employmentTypeFilter || employee?.employee_type === employmentTypeFilter;
      const matchesStatus = !statusFilter || employee?.status === statusFilter;

      // Date filter logic
      const employeeDate = employee?.joining_date ? new Date(employee?.joining_date) : null;
      const matchesStartDate = !startDate || (employeeDate && employeeDate >= new Date(startDate));
      const matchesEndDate = !endDate || (employeeDate && employeeDate <= new Date(endDate));

      // Only show known employees (non-strangers)
      const isKnownEmployee = !employee?.isUnkown;

      // Apply isUnknownFilter
      const matchesUnknownFilter = !isUnknownFilter ||
        (isUnknownFilter === 'known' && !employee?.isUnkown) ||
        (isUnknownFilter === 'unknown' && employee?.isUnkown);

      return (
        matchesSearch &&
        matchesDepartment &&
        matchesEmploymentType &&
        matchesStatus &&
        matchesStartDate &&
        matchesEndDate &&
        isKnownEmployee &&
        matchesUnknownFilter
      );
    });

    // Sorting logic
    filtered?.sort((a, b) => {
      let aValue, bValue;
      if (sortConfig?.key === "name") {
        aValue = `${a?.first_name} ${a?.last_name}`;
        bValue = `${b?.first_name} ${b?.last_name}`;
      } else {
        aValue = a?.[sortConfig?.key];
        bValue = b?.[sortConfig?.key];
      }

      if (sortConfig?.key === "lastActivity") {
        return sortConfig?.direction === "asc"
          ? new Date(aValue) - new Date(bValue)
          : new Date(bValue) - new Date(aValue);
      }

      if (typeof aValue === "string") {
        return sortConfig?.direction === "asc"
          ? aValue?.localeCompare(bValue)
          : bValue?.localeCompare(aValue);
      }

      return sortConfig?.direction === "asc" ? aValue - bValue : bValue - aValue;
    });

    return filtered;
  }, [
    employees,
    searchTerm,
    departmentFilter,
    employmentTypeFilter,
    statusFilter,
    startDate,
    endDate,
    sortConfig,
    isUnknownFilter,
  ]);

  // Pagination
  const paginatedEmployees = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedEmployees?.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedEmployees, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedEmployees?.length / itemsPerPage);

  // Event Handlers
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev?.key === key && prev?.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleSelectEmployee = (employeeId, isSelected) => {
    setSelectedEmployees((prev) =>
      isSelected ? [...prev, employeeId] : prev?.filter((id) => id !== employeeId)
    );
  };

  const handleSelectAll = (isSelected) => {
    setSelectedEmployees(isSelected ? paginatedEmployees?.map((emp) => emp?.employee_id) : []);
  };

  const handleAddEmployee = () => {
    setModalState({ isOpen: true, employee: null, mode: "add" });
  };

  const handleEditEmployee = (employee) => {
    setModalState({ isOpen: true, employee, mode: "edit" });
  };

  const handleViewEmployee = (employee) => {
    setModalState({ isOpen: true, employee, mode: "view" });
  };

  const handleUploadPhoto = (employee) => {
    console.log("Upload photo for:", `${employee?.firstName} ${employee?.lastName}`);
  };

  const handleToggleStatus = async (employee) => {
    const newStatus = employee?.status === "active" ? "inactive" : "active";

    try {
      await employeeApi.toggleEmployeeStatus(employee);

      // Update local state
      setEmployees((prev) =>
        prev?.map((emp) => (emp?.id === employee?.id ? { ...emp, status: newStatus } : emp))
      );

      const action = employee?.status !== 'active' ? 'activated' : 'deactivated';
      showNotification(`Employee ${action} successfully`, "success");
    } catch (error) {
      console.error("Error toggling employee status:", error);
      showNotification("Failed to toggle employee status", "error");
    }
  };

  const handleBulkAction = async (action, employeeIds) => {
    if (employeeIds.length === 0) {
      setSelectedEmployees([]);
      return;
    }

    try {
      switch (action) {
        case "activate":
          await employeeApi.activateEmployees(employeeIds);
          setEmployees((prev) =>
            prev?.map((emp) =>
              employeeIds?.includes(emp?.id) ? { ...emp, status: "active" } : emp
            )
          );
          showNotification(`${employeeIds.length} employee(s) activated successfully`, "success");
          break;

        case "deactivate":
          await employeeApi.deactivateEmployees(employeeIds);
          setEmployees((prev) =>
            prev?.map((emp) =>
              employeeIds?.includes(emp?.id) ? { ...emp, status: "inactive" } : emp
            )
          );
          showNotification(`${employeeIds.length} employee(s) deactivated successfully`, "success");
          break;

        case "suspend":
          await employeeApi.suspendEmployees(employeeIds);
          setEmployees((prev) =>
            prev?.map((emp) =>
              employeeIds?.includes(emp?.id) ? { ...emp, status: "suspended" } : emp
            )
          );
          showNotification(`${employeeIds?.length} employee(s) suspended successfully`, "success");
          break;

        case "delete":
          await employeeApi.deleteEmployees(employeeIds);
          setEmployees((prev) => prev?.filter((emp) => !employeeIds?.includes(emp?.id)));
          showNotification(`${employeeIds?.length} employee(s) deleted successfully`, "success");
          break;

        case "export":
          try {
            const blob = await employeeApi.exportSelectedEmployees(employeeIds);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              `selected_employees_${new Date().toISOString().slice(0, 10)}.csv`
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            showNotification("Selected employees exported successfully", "success");
          } catch (err) {
            console.error("Export failed:", err);
            showNotification("Failed to export selected employees", "error");
          }
          break;

        case "clear-selection":
          setSelectedEmployees([]);
          break;
      }

      if (action !== "clear-selection") {
        setSelectedEmployees([]);
      }
    } catch (error) {
      console.error("Error in bulk action:", error);
      const errorMessage = "Failed to perform bulk action.";
      showNotification(errorMessage, "error");
    } finally {
      loadEmployees();
    }
  };

  const handleSaveEmployee = async (employeeData) => {
    try {
      if (modalState?.mode === "add") {
        const newEmployee = await employeeApi.createEmployee(employeeData);

        setEmployees((prev) => [...prev, newEmployee]);
        showNotification("Employee created successfully", "success");

      } else if (modalState?.mode === "edit") {
        const updatedEmployee = await employeeApi.updateEmployee(
          modalState?.employee?.employee_id,
          employeeData
        );

        setEmployees((prev) =>
          prev.map((emp) =>
            emp.employee_id === modalState.employee.employee_id
              ? updatedEmployee
              : emp
          )
        );
        showNotification("Employee updated successfully", "success");
      }

      // ✅ CLOSE MODAL ONLY ON SUCCESS
      setModalState({ isOpen: false, employee: null, mode: "view" });
      setIsEditing(false);

      // ✅ reload only on success
      loadEmployees();

    } catch (error) {
      console.error("Error saving employee:", error);

      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        "Failed to save employee.";

      showNotification(errorMessage, "error");

      // ✅ VERY IMPORTANT
      throw error; // <-- modal-ku error theriyanum
    }
  };


  const handleClearFilters = () => {
    setSearchTerm("");
    setDepartmentFilter("");
    setEmploymentTypeFilter("");
    setStatusFilter("");
    setStartDate("");
    setEndDate("");
    setIsUnknownFilter("");
    setCurrentPage(1);
  };

  const handleExport = async () => {
    try {
      const blob = await employeeApi.exportAllEmployees();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `employees_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showNotification("Employee data exported successfully", "success");
    } catch (err) {
      console.error("Export failed:", err);
      showNotification("Failed to export employee data", "error");
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedEmployees([]);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    setSelectedEmployees([]);
  };

  return {
    // State
    isNavCollapsed,
    employees,
    departments,
    selectedEmployees,
    searchTerm,
    filtersCollapsed,
    departmentFilter,
    employmentTypeFilter,
    statusFilter,
    startDate,
    endDate,
    sortConfig,
    currentPage,
    itemsPerPage,
    notification,
    modalState,
    isEditing,
    loading,
    isUnknownFilter,
    filteredAndSortedEmployees,
    paginatedEmployees,
    totalPages,

    // State setters
    setIsNavCollapsed,
    setSearchTerm,
    setFiltersCollapsed,
    setDepartmentFilter,
    setEmploymentTypeFilter,
    setStatusFilter,
    setStartDate,
    setEndDate,
    setSortConfig,
    setCurrentPage,
    setItemsPerPage,
    setModalState,
    setIsEditing,
    setIsUnknownFilter,

    // Functions
    closeNotification,
    showNotification,
    loadEmployees,
    loadDepartments,
    handleSort,
    handleSelectEmployee,
    handleSelectAll,
    handleAddEmployee,
    handleEditEmployee,
    handleViewEmployee,
    handleUploadPhoto,
    handleToggleStatus,
    handleBulkAction,
    handleSaveEmployee,
    handleClearFilters,
    handleExport,
    handlePageChange,
    handleItemsPerPageChange
  };
};

export default useEmployeeManagementLogic;